import React, { useState, useMemo, useCallback } from "react";
import { entities, getDisplayMessage } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  Plus,
  Eye,
  Trash2,
  Search,
  Lightbulb,
  BarChart3,
  Award,
  XCircle,
  CheckCircle,
  FileText
} from "lucide-react";
import { format } from "date-fns";
import { safeParseISO } from "@/utils";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import {
  getClassificacoesEdital,
  getLinhaEmpresaPadrao,
  getLinhaVencedora,
} from "@/lib/dashboardComercialUtils";
import { segmentosClassificacaoParaUi } from "@/lib/classificacaoPregaoUtils";

/** Converte data_abertura do edital para yyyy-MM-dd (input type=date). */
function dataAberturaParaInputDate(edital) {
  const raw = edital?.data_abertura ?? edital?.dataAbertura;
  if (raw == null || typeof raw !== "string") return "";
  const t = raw.trim();
  if (!t || /n[ãa]o\s+localizado/i.test(t)) return "";
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const br = t.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (br) {
    const d = br[1].padStart(2, "0");
    const m = br[2].padStart(2, "0");
    return `${br[3]}-${m}-${d}`;
  }
  const d = new Date(t.includes("T") ? t : `${t}T12:00:00`);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

/** Preenche campos da lição a partir do edital (planilha de classificação e data de abertura). */
function deriveCamposLicaoFromEdital(edital) {
  const data_pregao = dataAberturaParaInputDate(edital);
  let classificacao = "";
  let vencedor = "";
  let valor_vencedor = "";

  const raw = edital.classificacoes;
  if (Array.isArray(raw) && raw.length > 0) {
    const segmentos = segmentosClassificacaoParaUi(raw);
    let preenchidoPorGrupo = false;
    for (const seg of segmentos) {
      const clsSeg = getClassificacoesEdital({ classificacoes: seg.linhas });
      if (clsSeg.length === 0) continue;
      const linhaPadrao = getLinhaEmpresaPadrao(clsSeg);
      if (!linhaPadrao) continue;
      preenchidoPorGrupo = true;
      if (linhaPadrao.classificacao != null && String(linhaPadrao.classificacao).trim() !== "") {
        classificacao = String(linhaPadrao.classificacao).trim();
      }
      const linhaVenc = getLinhaVencedora(clsSeg);
      if (linhaVenc) {
        vencedor =
          String(linhaVenc.empresa ?? "").trim() ||
          String(linhaVenc.descricao ?? "").trim();
        const v = Number(linhaVenc.valor_final ?? linhaVenc.valorFinal ?? 0);
        if (Number.isFinite(v) && v > 0) {
          valor_vencedor = String(v);
        }
      }
      break;
    }
    if (!preenchidoPorGrupo) {
      const cls = getClassificacoesEdital(edital);
      if (cls.length > 0) {
        const linhaPadrao = getLinhaEmpresaPadrao(cls);
        if (linhaPadrao?.classificacao != null && String(linhaPadrao.classificacao).trim() !== "") {
          classificacao = String(linhaPadrao.classificacao).trim();
        }
        const linhaVenc = getLinhaVencedora(cls);
        if (linhaVenc) {
          vencedor =
            String(linhaVenc.empresa ?? "").trim() ||
            String(linhaVenc.descricao ?? "").trim();
          const v = Number(linhaVenc.valor_final ?? linhaVenc.valorFinal ?? 0);
          if (Number.isFinite(v) && v > 0) {
            valor_vencedor = String(v);
          }
        }
      }
    }
  }

  if (!classificacao && edital.classificacao != null && edital.classificacao !== "") {
    const c = String(edital.classificacao).trim();
    if (c !== "0") classificacao = c;
  }

  return { data_pregao, classificacao, vencedor, valor_vencedor };
}
import SelecionarPregaoModal from "@/components/propostas/SelecionarPregaoModal";

export default function LearnedLessonsPage() {
  const queryClient = useQueryClient();
  const [dialogAberto, setDialogAberto] = useState(false);
  const [dialogExclusaoAberto, setDialogExclusaoAberto] = useState(false);
  const [dialogRiscosAberto, setDialogRiscosAberto] = useState(false);
  const [licaoSelecionada, setLicaoSelecionada] = useState(null);
  const [busca, setBusca] = useState("");
  const [licaoParaExcluir, setLicaoParaExcluir] = useState(null);
  const [modalEditalAberto, setModalEditalAberto] = useState(false);

  // Estados do formulário
  const [formData, setFormData] = useState({
    instituicao: "",
    edital_id: "",
    edital_nome: "",
    data_pregao: "",
    classificacao: "",
    categoria: "",
    vencedor: "",
    valor_nossa_empresa: "",
    valor_vencedor: "",
    fatos_relevantes: "",
    licoes_aprendidas: "",
    observacoes: ""
  });

  // Carregar lições
  const { data: licoes = [], isLoading, isError, error } = useQuery({
    queryKey: ['licoes-aprendidas'],
    queryFn: () => entities.LicaoAprendida.list('-createdAt'),
    retry: 1,
  });

  // Carregar editais para select
  const { data: editais = [] } = useQuery({
    queryKey: ['editais-select'],
    queryFn: async () => {
      const data = await entities.Edital.list('-createdAt', 100);
      return (Array.isArray(data) ? data : []).filter(e => e.status_aprovacao === 'aprovar_seguir');
    },
  });

  const resetForm = useCallback(() => {
    setFormData({
      instituicao: "",
      edital_id: "",
      edital_nome: "",
      data_pregao: "",
      classificacao: "",
      categoria: "",
      vencedor: "",
      valor_nossa_empresa: "",
      valor_vencedor: "",
      fatos_relevantes: "",
      licoes_aprendidas: "",
      observacoes: ""
    });
  }, []);

  const createMutation = useMutation({
    mutationFn: (data) => entities.LicaoAprendida.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licoes-aprendidas'] });
      setDialogAberto(false);
      resetForm();
      toast.success("Lição aprendida cadastrada com sucesso!");
    },
    onError: (error) => toast.error(getDisplayMessage(error, "Erro ao cadastrar lição. Tente novamente.")),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => entities.LicaoAprendida.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licoes-aprendidas'] });
      setDialogAberto(false);
      setLicaoSelecionada(null);
      resetForm();
      toast.success("Lição atualizada com sucesso!");
    },
    onError: (error) => toast.error(getDisplayMessage(error, "Erro ao atualizar lição. Tente novamente.")),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => entities.LicaoAprendida.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licoes-aprendidas'] });
      setDialogExclusaoAberto(false);
      setLicaoParaExcluir(null);
      toast.success("Lição excluída com sucesso!");
    },
    onError: (error) => toast.error(getDisplayMessage(error, "Erro ao excluir lição. Tente novamente.")),
  });

  const handleAbrirDialog = (licao = null) => {
    if (licao) {
      setLicaoSelecionada(licao);
      setFormData({
        instituicao: licao.instituicao || "",
        edital_id: licao.edital_id || "",
        edital_nome: licao.edital_nome || "",
        data_pregao: licao.data_pregao || "",
        classificacao: licao.classificacao || "",
        categoria: licao.categoria || "",
        vencedor: licao.vencedor || "",
        valor_nossa_empresa: licao.valor_nossa_empresa || "",
        valor_vencedor: licao.valor_vencedor || "",
        fatos_relevantes: licao.fatos_relevantes || "",
        licoes_aprendidas: licao.licoes_aprendidas || "",
        observacoes: licao.observacoes || ""
      });
    } else {
      setLicaoSelecionada(null);
      resetForm();
    }
    setDialogAberto(true);
  };

  const handleSalvar = () => {
    if (licaoSelecionada) {
      updateMutation.mutate({ id: licaoSelecionada.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleExcluir = (licao) => {
    setLicaoParaExcluir(licao);
    setDialogExclusaoAberto(true);
  };

  const handleConfirmarExclusao = () => {
    if (licaoParaExcluir) {
      deleteMutation.mutate(licaoParaExcluir.id);
    }
  };

  const handleSelecionarEdital = (editalOuId) => {
    const edital =
      editalOuId != null && typeof editalOuId === "object"
        ? editalOuId
        : editais.find((e) => e.id === editalOuId);
    if (!edital) return;

    const derived = deriveCamposLicaoFromEdital(edital);
    setFormData((prev) => ({
      ...prev,
      edital_id: edital.id,
      edital_nome: edital.titulo_licitacao || edital.nome_arquivo || "",
      instituicao: edital.instituicao || prev.instituicao,
      data_pregao: derived.data_pregao,
      classificacao: derived.classificacao,
      vencedor: derived.vencedor,
      valor_vencedor: derived.valor_vencedor,
    }));
  };

  // Cálculos do Dashboard
  const kpis = useMemo(() => {
    const total = licoes.length;
    const vitorias = licoes.filter(l => l.classificacao === "1º").length;
    const taxaVitorias = total > 0 ? ((vitorias / total) * 100).toFixed(1) : 0;
    
    const licoesRiscosAltos = licoes.filter(l => 
      l.fatos_relevantes?.toLowerCase().includes('certificação') ||
      l.fatos_relevantes?.toLowerCase().includes('certificado') ||
      l.fatos_relevantes?.toLowerCase().includes('documentação') ||
      l.fatos_relevantes?.toLowerCase().includes('documento') ||
      l.fatos_relevantes?.toLowerCase().includes('inabilitado') ||
      l.fatos_relevantes?.toLowerCase().includes('inabilitação')
    );

    const classificacoes = licoes
      .filter(l => l.classificacao && l.classificacao.match(/^\d+/))
      .map(l => parseInt(l.classificacao.match(/^\d+/)[0]));
    
    const posicaoMedia = classificacoes.length > 0 
      ? (classificacoes.reduce((a, b) => a + b, 0) / classificacoes.length).toFixed(1)
      : "-";

    return { total, taxaVitorias, riscosAltos: licoesRiscosAltos.length, licoesRiscosAltos, posicaoMedia };
  }, [licoes]);

  // Análise de motivos de perda
  const motivosPerda = useMemo(() => {
    const motivos = {
      'Preço': 0,
      'Documentação': 0,
      'Técnica': 0,
      'Atestados': 0,
      'Genérico': 0
    };

    licoes.forEach(licao => {
      const fatos = (licao.fatos_relevantes || "").toLowerCase();
      if (fatos.includes('preço') || fatos.includes('valor')) motivos['Preço']++;
      if (fatos.includes('documentação') || fatos.includes('documento')) motivos['Documentação']++;
      if (fatos.includes('técnica') || fatos.includes('especificação')) motivos['Técnica']++;
      if (fatos.includes('atestado') || fatos.includes('capacidade')) motivos['Atestados']++;
      if (fatos.includes('genérico') || fatos.includes('generico')) motivos['Genérico']++;
    });

    const maxValue = Math.max(...Object.values(motivos), 1);
    return Object.entries(motivos).map(([nome, valor]) => ({
      nome,
      valor,
      percentual: (valor / maxValue) * 100
    }));
  }, [licoes]);

  // Gap competitivo
  const gapCompetitivo = useMemo(() => {
    return licoes
      .filter(l => l.valor_nossa_empresa && l.valor_vencedor && l.classificacao !== "1º")
      .slice(0, 10)
      .map(licao => {
        const gap = ((licao.valor_nossa_empresa - licao.valor_vencedor) / licao.valor_vencedor) * 100;
        return {
          nome: licao.edital_nome || licao.instituicao,
          gap: gap.toFixed(1)
        };
      });
  }, [licoes]);

  const licoesFiltradasEPesquisadas = useMemo(() => {
    return licoes.filter(licao => {
      if (!busca) return true;
      const termo = busca.toLowerCase();
      return (
        licao.instituicao?.toLowerCase().includes(termo) ||
        licao.edital_nome?.toLowerCase().includes(termo) ||
        licao.classificacao?.toLowerCase().includes(termo) ||
        licao.fatos_relevantes?.toLowerCase().includes(termo)
      );
    });
  }, [licoes, busca]);

  const formatDate = (dateString) => {
    const d = dateString != null && typeof dateString === 'string' ? safeParseISO(dateString) : null;
    return d ? format(d, "dd/MM/yyyy", { locale: ptBR }) : '-';
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="p-4 md:p-8 min-h-screen" data-tour="tour-LearnedLessons-root">
      <div className="max-w-7xl mx-auto">
        {isError && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Não foi possível carregar as lições aprendidas. {error?.message || 'Verifique sua conexão e tente novamente.'}
            </AlertDescription>
          </Alert>
        )}
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Lições Aprendidas
            </h1>
            <p className="text-slate-600">
              Aprenda com o passado e tome decisões estratégicas baseadas em dados históricos
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => handleAbrirDialog()}
              className="bg-blue-600 hover:bg-blue-700"
              data-tour="tour-LearnedLessons-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Lição
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Analisado</p>
                  <p className="text-3xl font-bold text-slate-900">{kpis.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Taxa de Vitórias</p>
                  <p className="text-3xl font-bold text-green-600">{kpis.taxaVitorias}%</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Award className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-none shadow-md cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => kpis.riscosAltos > 0 && setDialogRiscosAberto(true)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Riscos Altos</p>
                  <p className="text-3xl font-bold text-red-600">{kpis.riscosAltos}</p>
                  {kpis.riscosAltos > 0 && (
                    <p className="text-xs text-slate-500 mt-1">Clique para ver detalhes</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Posição Média</p>
                  <p className="text-3xl font-bold text-slate-900">{kpis.posicaoMedia}º</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Motivos de Perda */}
          <Card className="border-none shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Principais Motivos de Perda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {motivosPerda.map((motivo, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{motivo.nome}</span>
                      <span className="text-sm text-slate-600">{motivo.valor}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-red-500 to-orange-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${motivo.percentual}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Gap Competitivo */}
          <Card className="border-none shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-600" />
                Gap Competitivo (% acima do vencedor)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {gapCompetitivo.length > 0 ? (
                  gapCompetitivo.map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs text-slate-600 truncate max-w-[200px]">
                          {item.nome}
                        </span>
                        <span className="text-xs font-medium text-red-600">+{item.gap}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-red-500 h-2 rounded-full"
                          style={{ width: `${Math.min(Math.abs(item.gap), 100)}%` }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">
                    Nenhum dado disponível ainda
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Histórico */}
        <Card className="border-none shadow-md">
          <CardHeader>
            <CardTitle>Histórico de Licitações</CardTitle>
            <CardDescription>Registros de experiências anteriores</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Busca */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                placeholder="Buscar por instituição, edital, classificação..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-12"
              />
            </div>

            {/* Tabela */}
            <div className="space-y-3">
              {isLoading ? (
                <p className="text-center py-8 text-slate-500">Carregando...</p>
              ) : licoesFiltradasEPesquisadas.length === 0 ? (
                <p className="text-center py-8 text-slate-500">
                  Nenhuma lição cadastrada ainda
                </p>
              ) : (
                licoesFiltradasEPesquisadas.map((licao) => (
                  <Card key={licao.id} className="border border-slate-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 mb-2">
                            {licao.edital_nome || licao.instituicao}
                          </h4>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <Badge variant="outline" className="bg-slate-50">
                              {licao.instituicao}
                            </Badge>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              {formatDate(licao.data_pregao)}
                            </Badge>
                            <Badge 
                              variant="outline"
                              className={
                                licao.classificacao === "1º"
                                  ? "bg-green-50 text-green-700"
                                  : "bg-amber-50 text-amber-700"
                              }
                            >
                              {licao.classificacao}
                            </Badge>
                            <Badge variant="outline" className="bg-purple-50 text-purple-700">
                              {licao.categoria}
                            </Badge>
                          </div>
                          {licao.fatos_relevantes && (
                            <p className="text-sm text-slate-600 line-clamp-2">
                              {licao.fatos_relevantes}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAbrirDialog(licao)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExcluir(licao)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Cadastro/Edição */}
      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {licaoSelecionada ? "Editar Lição Aprendida" : "Nova Lição Aprendida"}
            </DialogTitle>
            <DialogDescription className="text-base">
              Registre experiências de licitações anteriores
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            <div className="grid grid-cols-2 gap-5">
              <div className="col-span-2">
                <Label className="text-base font-medium">Selecionar Edital Cadastrado (Opcional)</Label>
                <p className="text-sm text-slate-600 mb-2">
                  Busque por instituição, número do pregão, processo ou objeto e clique em &quot;Consultar pregão&quot; para selecionar.
                </p>
                <div className="flex gap-2">
                  <div className="flex-1 rounded-lg border border-input bg-muted/40 px-3 py-2 text-sm text-slate-700 min-h-[44px] flex items-center">
                    {formData.edital_id
                      ? (() => {
                          const edital = (Array.isArray(editais) ? editais : []).find((e) => e.id === formData.edital_id);
                          if (!edital)
                            return `Edital selecionado (ID: ${formData.edital_id.slice(0, 8)}…)`;
                          const inst = edital.instituicao || edital.titulo_licitacao || edital.nome_arquivo || "";
                          const pe = edital.numero_pregao ? ` • ${edital.numero_pregao}` : "";
                          const proc = edital.numero_processo ? ` • ${edital.numero_processo}` : "";
                          return inst ? `${inst}${pe}${proc}` : `Edital (ID: ${edital.id.slice(0, 8)}…)`;
                        })()
                      : "Nenhum edital selecionado — clique em Consultar pregão para buscar"}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setModalEditalAberto(true)}
                    className="shrink-0 gap-2 h-11"
                  >
                    <Search className="w-4 h-4" />
                    Consultar pregão
                  </Button>
                </div>
                <SelecionarPregaoModal
                  open={modalEditalAberto}
                  onOpenChange={setModalEditalAberto}
                  editais={editais}
                  editalSelecionadoId={formData.edital_id || null}
                  onSelecionar={(edital) => handleSelecionarEdital(edital)}
                  statusFiltro="todos"
                />
              </div>

              <div className="col-span-2">
                <Label className="text-base font-medium">Instituição *</Label>
                <Input
                  value={formData.instituicao}
                  onChange={(e) => setFormData({...formData, instituicao: e.target.value})}
                  placeholder="Nome da instituição"
                  className="h-11 text-base"
                />
              </div>

              <div className="col-span-2">
                <Label className="text-base font-medium">Nome do Edital</Label>
                <Input
                  value={formData.edital_nome}
                  onChange={(e) => setFormData({...formData, edital_nome: e.target.value})}
                  placeholder="Título do edital"
                  className="h-11 text-base"
                />
              </div>

              <div>
                <Label className="text-base font-medium">Data do Pregão *</Label>
                <Input
                  type="date"
                  value={formData.data_pregao}
                  onChange={(e) => setFormData({...formData, data_pregao: e.target.value})}
                  className="h-11 text-base"
                />
              </div>

              <div>
                <Label className="text-base font-medium">Classificação *</Label>
                <Input
                  value={formData.classificacao}
                  onChange={(e) => setFormData({...formData, classificacao: e.target.value})}
                  placeholder="1º, 2º, 3º ou Não classificado"
                  className="h-11 text-base"
                />
              </div>

              <div>
                <Label className="text-base font-medium">Categoria *</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(value) => setFormData({...formData, categoria: value})}
                >
                  <SelectTrigger className="h-11 text-base">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Preço" className="text-base">Preço</SelectItem>
                    <SelectItem value="Documentação" className="text-base">Documentação</SelectItem>
                    <SelectItem value="Técnica" className="text-base">Técnica</SelectItem>
                    <SelectItem value="Outro" className="text-base">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-base font-medium">Vencedor</Label>
                <Input
                  value={formData.vencedor}
                  onChange={(e) => setFormData({...formData, vencedor: e.target.value})}
                  placeholder="Nome do vencedor"
                  className="h-11 text-base"
                />
              </div>

              <div>
                <Label className="text-base font-medium">Valor Nossa Empresa</Label>
                <Input
                  type="number"
                  value={formData.valor_nossa_empresa}
                  onChange={(e) => setFormData({...formData, valor_nossa_empresa: e.target.value})}
                  placeholder="0.00"
                  className="h-11 text-base"
                />
              </div>

              <div>
                <Label className="text-base font-medium">Valor Vencedor</Label>
                <Input
                  type="number"
                  value={formData.valor_vencedor}
                  onChange={(e) => setFormData({...formData, valor_vencedor: e.target.value})}
                  placeholder="0.00"
                  className="h-11 text-base"
                />
              </div>

              <div className="col-span-2">
                <Label className="text-base font-medium">Fatos Relevantes</Label>
                <Textarea
                  value={formData.fatos_relevantes}
                  onChange={(e) => setFormData({...formData, fatos_relevantes: e.target.value})}
                  placeholder="O que aconteceu que levou a esse resultado?"
                  rows={5}
                  className="text-base resize-none"
                />
              </div>

              <div className="col-span-2">
                <Label className="text-base font-medium">Lições Aprendidas</Label>
                <Textarea
                  value={formData.licoes_aprendidas}
                  onChange={(e) => setFormData({...formData, licoes_aprendidas: e.target.value})}
                  placeholder="O que aprendemos com isso?"
                  rows={5}
                  className="text-base resize-none"
                />
              </div>

              <div className="col-span-2">
                <Label className="text-base font-medium">Observações</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observações adicionais"
                  rows={4}
                  className="text-base resize-none"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogAberto(false)} className="h-11 text-base">
              Cancelar
            </Button>
            <Button 
              onClick={handleSalvar}
              disabled={!formData.instituicao || !formData.data_pregao || !formData.classificacao || !formData.categoria}
              className="h-11 text-base"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Riscos Altos */}
      <Dialog open={dialogRiscosAberto} onOpenChange={setDialogRiscosAberto}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Licitações com Riscos Altos ({kpis.riscosAltos})
            </DialogTitle>
            <DialogDescription>
              Licitações que apresentaram problemas críticos como falta de certificação, documentação ou inabilitação
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {kpis.licoesRiscosAltos.map((licao) => (
              <Card 
                key={licao.id} 
                className="border border-red-200 hover:shadow-md transition-shadow cursor-pointer bg-red-50/30"
                onClick={() => {
                  setDialogRiscosAberto(false);
                  handleAbrirDialog(licao);
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-900 mb-2">
                        {licao.edital_nome || licao.instituicao}
                      </h4>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="outline" className="bg-slate-50">
                          {licao.instituicao}
                        </Badge>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          {formatDate(licao.data_pregao)}
                        </Badge>
                        <Badge 
                          variant="outline"
                          className="bg-amber-50 text-amber-700"
                        >
                          {licao.classificacao}
                        </Badge>
                        <Badge variant="outline" className="bg-red-50 text-red-700">
                          {licao.categoria}
                        </Badge>
                      </div>
                      {licao.fatos_relevantes && (
                        <div className="bg-white border border-red-200 rounded-lg p-3">
                          <p className="text-sm font-medium text-red-900 mb-1">Fatos Relevantes:</p>
                          <p className="text-sm text-slate-700">{licao.fatos_relevantes}</p>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDialogRiscosAberto(false);
                        handleAbrirDialog(licao);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogRiscosAberto(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Exclusão */}
      <AlertDialog open={dialogExclusaoAberto} onOpenChange={setDialogExclusaoAberto}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta lição aprendida? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarExclusao}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}